import React, { createContext, useContext, useState, useCallback } from "react";

const MeetingsContext = createContext();

export const useMeetings = () => {
  const context = useContext(MeetingsContext);
  if (!context) {
    throw new Error("useMeetings must be used within a MeetingsProvider");
  }
  return context;
};

export const MeetingsProvider = ({ children }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Helper function to merge meetings without duplicates
  const mergeMeetings = useCallback((existingMeetings, newMeetings) => {
    const existingIds = new Set(existingMeetings.map((m) => m.id));
    const uniqueNewMeetings = newMeetings.filter((m) => !existingIds.has(m.id));

    // Combine and sort by time
    const combined = [...existingMeetings, ...uniqueNewMeetings];
    return combined.sort((a, b) => {
      const timeA = a.start?.dateTime || a.start?.date || "";
      const timeB = b.start?.dateTime || b.start?.date || "";
      return timeA.localeCompare(timeB);
    });
  }, []);

  // Update meetings from API or WebSocket
  const updateMeetings = useCallback(
    (newMeetings, source = "unknown") => {
      console.log(`📅 Updating meetings from ${source}:`, newMeetings.length);

      setMeetings((prev) => {
        const updated = mergeMeetings(prev, newMeetings);
        setLastUpdated(new Date().toISOString());

        // Cache the meetings
        localStorage.setItem("cached_meetings", JSON.stringify(updated));
        localStorage.setItem("meetings_last_fetch", new Date().toISOString());
        localStorage.setItem("meetings_source", source);

        console.log(`✅ Context updated with ${updated.length} total meetings`);
        return updated;
      });
    },
    [mergeMeetings]
  );

  // Set meetings (replace all)
  const setMeetingsData = useCallback((newMeetings, source = "unknown") => {
    console.log(`📅 Setting meetings from ${source}:`, newMeetings.length);

    const sortedMeetings = newMeetings.sort((a, b) => {
      const timeA = a.start?.dateTime || a.start?.date || "";
      const timeB = b.start?.dateTime || b.start?.date || "";
      return timeA.localeCompare(timeB);
    });

    setMeetings(sortedMeetings);
    setLastUpdated(new Date().toISOString());

    // Cache the meetings
    localStorage.setItem("cached_meetings", JSON.stringify(sortedMeetings));
    localStorage.setItem("meetings_last_fetch", new Date().toISOString());
    localStorage.setItem("meetings_source", source);

    console.log(`✅ Context set with ${sortedMeetings.length} meetings`);
  }, []);

  // Add a single meeting
  const addMeeting = useCallback(
    (meeting) => {
      console.log(`📅 Adding single meeting:`, meeting.summary);

      setMeetings((prev) => {
        // Check if meeting already exists
        const meetingExists = prev.some((m) => m.id === meeting.id);
        if (meetingExists) {
          console.log("Meeting already exists in context, skipping duplicate");
          return prev;
        }

        const updated = mergeMeetings(prev, [meeting]);
        setLastUpdated(new Date().toISOString());

        // Cache the meetings
        localStorage.setItem("cached_meetings", JSON.stringify(updated));
        localStorage.setItem("meetings_last_fetch", new Date().toISOString());
        localStorage.setItem("meetings_source", "websocket");

        return updated;
      });
    },
    [mergeMeetings]
  );

  // Update a single meeting
  const updateMeeting = useCallback((updatedMeeting) => {
    console.log(`📅 Updating single meeting:`, updatedMeeting.summary);

    setMeetings((prev) => {
      const updated = prev.map((meeting) =>
        meeting.id === updatedMeeting.id
          ? { ...meeting, ...updatedMeeting }
          : meeting
      );

      // Re-sort after update
      const sortedMeetings = updated.sort((a, b) => {
        const timeA = a.start?.dateTime || a.start?.date || "";
        const timeB = b.start?.dateTime || b.start?.date || "";
        return timeA.localeCompare(timeB);
      });

      setLastUpdated(new Date().toISOString());

      // Cache the meetings
      localStorage.setItem("cached_meetings", JSON.stringify(sortedMeetings));
      localStorage.setItem("meetings_last_fetch", new Date().toISOString());
      localStorage.setItem("meetings_source", "websocket");

      return sortedMeetings;
    });
  }, []);

  // Remove a meeting
  const removeMeeting = useCallback((meetingId) => {
    console.log(`📅 Removing meeting:`, meetingId);

    setMeetings((prev) => {
      const updated = prev.filter((m) => m.id !== meetingId);
      setLastUpdated(new Date().toISOString());

      // Cache the meetings
      localStorage.setItem("cached_meetings", JSON.stringify(updated));
      localStorage.setItem("meetings_last_fetch", new Date().toISOString());
      localStorage.setItem("meetings_source", "websocket");

      return updated;
    });
  }, []);

  // Clear all meetings
  const clearMeetings = useCallback(() => {
    console.log(`📅 Clearing all meetings`);
    setMeetings([]);
    setLastUpdated(new Date().toISOString());
    localStorage.removeItem("cached_meetings");
    localStorage.removeItem("meetings_last_fetch");
    localStorage.removeItem("meetings_source");
  }, []);

  // Load meetings from cache
  const loadFromCache = useCallback(() => {
    try {
      const cachedMeetings = localStorage.getItem("cached_meetings");
      if (cachedMeetings) {
        const parsedMeetings = JSON.parse(cachedMeetings);
        if (Array.isArray(parsedMeetings)) {
          setMeetings(parsedMeetings);
          setLastUpdated(localStorage.getItem("meetings_last_fetch"));
          console.log(
            `📦 Loaded ${parsedMeetings.length} meetings from cache to context`
          );
          return true;
        }
      }
    } catch (error) {
      console.error("Error loading meetings from cache:", error);
    }
    return false;
  }, []);

  const value = {
    meetings,
    loading,
    error,
    lastUpdated,
    setLoading,
    setError,
    updateMeetings,
    setMeetingsData,
    addMeeting,
    updateMeeting,
    removeMeeting,
    clearMeetings,
    loadFromCache,
  };

  return (
    <MeetingsContext.Provider value={value}>
      {children}
    </MeetingsContext.Provider>
  );
};
